import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { Dialog, DialogContent } from "@/components/_atoms/dialog";
import { Popover, PopoverPortalContent, PopoverTrigger } from "@/components/_atoms/popover";

import { useDatatableContext } from "../use-datatable-context";

import { FilterCommandConfiguration } from "./filter-command-view";
import { FilterCommandView } from "./filter-command-view";

export interface DatatableFilterDropdownProps extends PropsWithChildren {
  asChild?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
}

export const DatatableFilterDropdown: FC<DatatableFilterDropdownProps> = ({ children, side, align, asChild }) => {
  const { table, state, lock, getConfigurationForColumn } = useDatatableContext();
  const { columnFilters } = state;

  const [showPopover, setShowPopover] = useState(false);
  const [replacePopoverContentView, setReplacePopoverContentView] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFilterOption, setSelectedFilterOption] = useState<FilterCommandConfiguration | null>(null);

  const offerableColumnList = useMemo(() => {
    return table
      .getAllColumns()
      .filter(
        (column) =>
          column.getCanFilter() &&
          lock.canOfferColumn(column) &&
          !columnFilters.some((columnFilter) => columnFilter.id === column.id),
      );
  }, [table, columnFilters, lock]);

  const handleFinished = useCallback(() => {
    setShowPopover(false);
    setShowModal(false);
    setSelectedFilterOption(null);
  }, []);

  useEffect(() => {
    if (showPopover === true) {
      return;
    }

    setReplacePopoverContentView(false);
  }, [showPopover]);

  useEffect(() => {
    if (showModal === true) {
      return;
    }

    setSelectedFilterOption(null);
  }, [showModal]);

  const filterOptions: FilterCommandConfiguration[] = useMemo(() => {
    return offerableColumnList.flatMap((column) => {
      const columnConfiguration = getConfigurationForColumn(column.id);
      if (!columnConfiguration) {
        return [];
      }

      return {
        id: columnConfiguration.columnId,
        displayValue: columnConfiguration.getDisplayValue(),
        displayView: columnConfiguration.renderDisplayView(),
        configurationView: columnConfiguration.renderFilterConfigurationView(handleFinished),
        configurationViewType: columnConfiguration.getFilterConfigurationViewType(),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerableColumnList]);

  const handleFilterSelect = (filter: FilterCommandConfiguration) => {
    if (filter.configurationViewType === "none") {
      return;
    }

    if (filter.configurationViewType === "popover") {
      setSelectedFilterOption(filter);
      setReplacePopoverContentView(true);
      return;
    }

    if (filter.configurationViewType === "modal") {
      setSelectedFilterOption(filter);
      setShowModal(true);

      return;
    }
  };

  return (
    <>
      <Popover open={showPopover} onOpenChange={setShowPopover} modal={false}>
        <PopoverTrigger asChild={asChild}>{children}</PopoverTrigger>
        <PopoverPortalContent className="z-9999 p-0" side={side ?? "right"} align={align ?? "start"}>
          {replacePopoverContentView &&
            selectedFilterOption &&
            selectedFilterOption.configurationViewType === "popover" && <>{selectedFilterOption.configurationView}</>}
          {(!replacePopoverContentView ||
            !selectedFilterOption ||
            selectedFilterOption.configurationViewType !== "popover") && (
            <FilterCommandView filterList={filterOptions} onSelect={handleFilterSelect} />
          )}
        </PopoverPortalContent>
      </Popover>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          {selectedFilterOption && selectedFilterOption.configurationViewType === "modal" && (
            <>{selectedFilterOption.configurationView}</>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
